import { useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Send, CheckCircle2, Info } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";

const inputCls = "w-full bg-ink border border-bone/15 px-4 py-3.5 text-bone placeholder:text-ash/60 focus:border-saffron focus:outline-none transition-colors";

const TERMS = [
  "50% advance confirms your booking; balance on the day of the event",
  "For events outside Hyderabad, travel & hospitality are the client's responsibility",
  "Client arranges the sound system as per our technical rider and provides a drum kit",
];

export default function PrivatePage() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", event_date: "", city: "", message: "" });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/enquiries", { ...form, event_type: "Private Show", package: "" });
      setDone(true);
      window.scrollTo({ top: 0 });
    } catch (err) {
      toast.error(apiError(err, "Could not send your booking request"));
    }
    setSending(false);
  };

  return (
    <div className="min-h-screen bg-ink text-bone" data-testid="private-page">
      <div className="noise-overlay" aria-hidden="true" />
      <header className="px-6 md:px-12 h-[72px] flex items-center justify-between border-b border-bone/10 sticky top-0 bg-ink/90 backdrop-blur-xl z-40">
        <Link to="/" className="flex items-baseline gap-2" data-testid="private-logo">
          <span className="font-hindi text-2xl text-saffron">सुमिरन</span>
          <span className="font-display text-sm tracking-[0.25em] text-bone">THE BAND SUMIRAN</span>
        </Link>
        <div className="flex items-center gap-6">
          <Link to="/book" className="text-xs text-ash hover:text-saffron transition-colors" data-testid="private-book-link">Concert Tickets</Link>
          <Link to="/" className="text-xs text-ash hover:text-saffron transition-colors" data-testid="private-back-home">Back to site</Link>
        </div>
      </header>

      <main className="px-6 md:px-12 py-14 md:py-20 max-w-3xl mx-auto">
        {done ? (
          <div className="text-center py-16" data-testid="private-success">
            <CheckCircle2 size={48} className="mx-auto text-saffron" strokeWidth={1.5} />
            <h1 className="mt-6 font-serif text-4xl text-bone">Request received.</h1>
            <p className="mt-4 text-bone/70 max-w-md mx-auto leading-relaxed">
              Thank you, {form.name}. The band will reach out within a day to discuss your
              evening{form.city ? ` in ${form.city}` : ""} and share availability.
            </p>
            <Link to="/" className="mt-10 inline-block px-10 py-3.5 border border-bone/25 text-bone hover:border-saffron hover:text-saffron transition-colors" data-testid="private-success-home">
              Back to site
            </Link>
          </div>
        ) : (
          <>
            <p className="font-display text-xs tracking-[0.3em] text-saffron uppercase">Private Show Booking</p>
            <h1 className="mt-3 font-serif text-4xl sm:text-5xl text-bone leading-tight" data-testid="private-heading">
              Bring Sumiran to <em className="text-gold">your evening</em>
            </h1>
            <p className="mt-5 text-bone/65 leading-relaxed max-w-xl">
              Weddings, satsangs, anniversaries, corporate evenings — share your details and the
              band will call you back to plan the set, the lineup and the date.
            </p>

            <form onSubmit={submit} className="mt-10 space-y-4" data-testid="private-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <input required placeholder="Your name" value={form.name} onChange={set("name")} className={inputCls} data-testid="private-name-input" />
                <input required type="email" placeholder="Email" value={form.email} onChange={set("email")} className={inputCls} data-testid="private-email-input" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <input required placeholder="Phone (WhatsApp preferred)" value={form.phone} onChange={set("phone")} className={inputCls} data-testid="private-phone-input" />
                <input required placeholder="Event city" value={form.city} onChange={set("city")} className={inputCls} data-testid="private-city-input" />
              </div>
              <div>
                <label className="block text-xs font-display tracking-[0.2em] uppercase text-ash mb-1.5">Tentative event date</label>
                <input type="date" value={form.event_date} onChange={set("event_date")} className={inputCls} data-testid="private-date-input" />
              </div>
              <textarea rows={4} placeholder="Tell us about your gathering — occasion, guests, venue…" value={form.message} onChange={set("message")} className={inputCls} data-testid="private-message-input" />
              <button type="submit" disabled={sending}
                className="w-full py-4 bg-saffron text-ink font-medium tracking-wide hover:bg-gold transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="private-submit-btn">
                {sending ? <Loader2 size={18} className="animate-spin" /> : <Send size={16} />}
                {sending ? "Sending…" : "Request Booking"}
              </button>
            </form>

            <div className="mt-12 border border-bone/10 bg-surface/60 p-7" data-testid="private-terms">
              <p className="flex items-center gap-2 font-display text-[11px] tracking-[0.25em] text-ash uppercase"><Info size={13} className="text-saffron" /> Booking Terms</p>
              <ul className="mt-4 space-y-2.5">
                {TERMS.map((t) => (
                  <li key={t} className="flex items-start gap-3 text-sm text-bone/65">
                    <span className="w-1.5 h-1.5 bg-saffron mt-1.5 shrink-0" /> {t}
                  </li>
                ))}
              </ul>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
